import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Grid, TextField, Pagination } from '@mui/material';
import { SimpleCard, Breadcrumb } from 'app/components';
import axios from 'axios';
import dayjs from 'dayjs';
import { APP_SETTINGS } from '../../../config';

const PAGE_LIMIT = 12;

const UserMachineLogs = () => {
  const { userId, date } = useParams();
  const navigate = useNavigate();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userName, setUserName] = useState('');
  const [selectedDate, setSelectedDate] = useState(date);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      try {
        const userString = localStorage.getItem('user');
        if (!userString) throw new Error('Authentication Error: You are not logged in.');

        const loggedInUser = JSON.parse(userString);
        const token = loggedInUser?.token;
        if (!token) throw new Error('Authentication Error: Invalid session. Please log in again.');

        const response = await axios.get(`${APP_SETTINGS.API_PATH.attendance.getAll}/logs/${userId}/${selectedDate}`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            page: page,
            limit: PAGE_LIMIT
          }
        });

        if (response.data.succeeded) {
          setLogs(response.data.data.logs);
          setTotalPages(response.data.data.totalPages || 1);
          
          if (response.data.data.logs.length > 0 && !userName) {
            setUserName(response.data.data.logs[0].User.name);
          }
        } else {
          throw new Error(response.data.message || 'Failed to fetch logs.');
        }
      } catch (err) {
        setError(err.message || 'An error occurred while fetching logs.');
        setLogs([]);
      } finally {
        setLoading(false);
      }
    };

    if (userId && selectedDate) {
      fetchLogs();
    }
  }, [userId, selectedDate, userName, page]);

  const handleDateChange = (event) => {
    const newDate = event.target.value;
    setPage(1);
    setSelectedDate(newDate);
    navigate(`/machine-logs/${userId}/${newDate}`);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const getCheckTypeLabel = (type) => {
    switch (type) {
      case 0: return 'Check-In Punch';
      case 1: return 'Check-Out Punch';
      default: return 'Unknown Punch';
    }
  };

  return (
    <SimpleCard>
      <Breadcrumb routeSegments={[{ name: 'All Checks', path: '/all-check' }, { name: 'Machine Logs' }]} />
      <Box sx={{ py: 2 }}>
        <Grid container spacing={2} justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h5">
              Machine Punch Logs for {userName || '...'} on {dayjs(selectedDate).format('MMMM D, YYYY')}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Select Date"
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        {loading ? (
          <Box display="flex" justifyContent="center" my={4}><CircularProgress /></Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <>
            <Box sx={{ flexGrow: 1, overflow: 'auto', paddingBottom: '70px' }}>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Time of Punch</TableCell>
                      <TableCell>Punch Type</TableCell>
                      <TableCell>Notes from Worker</TableCell>
                    </TableRow> 
                  </TableHead>
                  <TableBody>
                    {logs.length > 0 ? (
                      logs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell>{dayjs(log.timestamp).format('hh:mm A')}</TableCell>
                          <TableCell>{getCheckTypeLabel(log.checkType)}</TableCell>
                          <TableCell>{log.notes}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          No machine logs found for this user on this date.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Box
              display="flex"
              justifyContent="center"
              sx={{
                position: 'sticky',
                bottom: 0,
                py: 2,
                backgroundColor: 'background.paper',
                zIndex: 1,
                boxShadow: '0 -2px 5px rgba(0,0,0,0.1)'
              }}
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          </>
        )}
      </Box>
    </SimpleCard>
  );
};

export default UserMachineLogs;